/**
 * zod-driven form validation.
 *
 * Forms validate against the same contract the API validates against, so the
 * client and the server can never disagree about what is acceptable. The client
 * check exists to give an answer without a round trip — it is never the only
 * check, and `apiFieldErrors` folds the server's verdict back into the same
 * shape when the two ever differ.
 */
import { fieldErrorsFromZod } from '@wend/shared';
import type { z } from 'zod';

import { ROOT_FIELD } from './api/errors';

/** Field path → message. `ROOT_FIELD` holds a form-level message. */
export type FormErrors = Record<string, string>;

export type ValidationResult<TSchema extends z.ZodTypeAny> =
  | { ok: true; data: z.output<TSchema> }
  | { ok: false; errors: FormErrors };

/** Runs a contract over form values. The first message per path wins. */
export function validate<TSchema extends z.ZodTypeAny>(
  schema: TSchema,
  value: unknown,
): ValidationResult<TSchema> {
  const parsed = schema.safeParse(value);
  if (parsed.success) {
    return { ok: true, data: parsed.data };
  }

  const errors: FormErrors = {};
  for (const fieldError of fieldErrorsFromZod(parsed.error)) {
    const key = fieldError.path === '' ? ROOT_FIELD : fieldError.path;
    if (errors[key] === undefined) {
      errors[key] = fieldError.message;
    }
  }
  return { ok: false, errors };
}

/** Removes one field's error, for clearing it as the user types. */
export function withoutError(errors: FormErrors, field: string): FormErrors {
  if (errors[field] === undefined && errors[ROOT_FIELD] === undefined) return errors;

  const next: FormErrors = { ...errors };
  delete next[field];
  // A form-level message is stale the moment the user edits anything.
  delete next[ROOT_FIELD];
  return next;
}
